(() => {
  'use strict';

  const SCHEMA = 'entity-navigation-recommendations/v1';
  const TYPES = ['brand', 'model', 'ref'];
  const MAX_BYTES = 64 * 1024;
  const MAX_AGE = 48 * 60 * 60 * 1000;
  const DAY = 24 * 60 * 60 * 1000;
  const loads = new Map();
  const initialized = new WeakSet();

  function entityKey(value, type) {
    return TYPES.includes(type) && typeof value === 'string' && value.length <= 160
      && new RegExp(`^${type}:[a-z0-9][a-z0-9:_-]*$`).test(value) ? value : null;
  }

  function entityUrl(value, type, origin) {
    if (!TYPES.includes(type) || typeof value !== 'string') return null;
    try {
      const url = new URL(value, origin);
      const pattern = new RegExp(`^/(?:[a-z]{2,3}(?:-[a-z0-9]{2,8})*/)?collections/${type}-[a-z0-9][a-z0-9-]{0,250}$`, 'i');
      return url.origin === origin && !url.username && !url.password && !url.search && !url.hash
        && pattern.test(url.pathname) ? url.pathname : null;
    } catch (_) { return null; }
  }

  function cacheUrl(value, origin) {
    if (typeof value !== 'string' || !value.trim()) return null;
    try {
      const url = new URL(value, origin);
      if (url.protocol !== 'https:' || url.username || url.password || url.hash
          || ![origin, 'https://cdn.shopify.com'].includes(url.origin) || !url.pathname.endsWith('.json')) return null;
      return url.href;
    } catch (_) { return null; }
  }

  function parseCache(data, origin, now = Date.now()) {
    if (!data || data.schema_version !== SCHEMA || data.ranking_basis !== 'verified_internal_navigation_events'
        || data.origin !== origin || !/^sha256-[a-f0-9]{64}$/.test(data.snapshot_id)
        || data.minimum_navigation_count !== 5 || data.minimum_observed_days !== 3
        || !Array.isArray(data.items) || data.items.length > 20 || data.item_count !== data.items.length) return null;
    const generated = Date.parse(data.generated_at), expires = Date.parse(data.expires_at);
    const start = Date.parse(data.window_start), end = Date.parse(data.window_end);
    const verified = Date.parse(data.links_verified_at);
    if (![generated, expires, start, end, verified, now].every(Number.isFinite)
        || end - start !== 30 * DAY || generated < end || generated > end + MAX_AGE
        || start % DAY !== 15 * 60 * 60 * 1000 || end % DAY !== 15 * 60 * 60 * 1000
        || verified > generated || verified < generated - MAX_AGE
        || generated > now + 300000 || expires <= now || expires <= generated
        || expires > generated + MAX_AGE || expires > verified + MAX_AGE) return null;
    const keys = new Set(), urls = new Set(), items = [];
    const counts = { brand: 0, model: 0, ref: 0 };
    for (const item of data.items) {
      const type = item?.page_type;
      const key = entityKey(item?.entity_key, type);
      const url = entityUrl(item?.canonical_url, type, origin);
      if (!key || !url || item.is_page_available !== true || item.search_index_state !== 'index'
          || keys.has(key) || urls.has(url) || typeof item.label !== 'string' || !item.label.trim()
          || [...item.label].length > 200 || /[\u0000-\u001f\u007f]/.test(item.label)
          || !Number.isSafeInteger(item.navigation_count) || item.navigation_count < 5
          || !Number.isSafeInteger(item.observed_days) || item.observed_days < 3 || item.observed_days > 30
          || item.observed_days > item.navigation_count) return null;
      if (++counts[type] > (type === 'ref' ? 8 : 6)) return null;
      keys.add(key); urls.add(url);
      items.push({ type, key, url, label: item.label, count: item.navigation_count, days: item.observed_days });
    }
    return { items, expires };
  }

  async function readText(response) {
    if (Number(response.headers.get('content-length')) > MAX_BYTES) return null;
    if (!response.body?.getReader) {
      const text = await response.text();
      return new TextEncoder().encode(text).byteLength <= MAX_BYTES ? text : null;
    }
    const reader = response.body.getReader();
    const decoder = new TextDecoder();
    let size = 0, text = '';
    try {
      while (true) {
        const chunk = await reader.read();
        if (chunk.done) return text + decoder.decode();
        size += chunk.value.byteLength;
        if (size > MAX_BYTES) { await reader.cancel(); return null; }
        text += decoder.decode(chunk.value, { stream: true });
      }
    } finally { reader.releaseLock(); }
  }

  function load(url, origin) {
    if (loads.has(url)) return loads.get(url);
    const controller = new AbortController();
    const timer = setTimeout(() => controller.abort(), 2500);
    const promise = fetch(url, { signal: controller.signal, credentials: 'omit', redirect: 'error', headers: { Accept: 'application/json' } })
      .then(async (response) => response.ok ? parseCache(JSON.parse(await readText(response)), origin) : null)
      .catch(() => null).finally(() => clearTimeout(timer));
    loads.set(url, promise);
    return promise;
  }

  function analyticsConsent(environment) {
    try {
      const privacy = environment.Shopify?.customerPrivacy;
      if (typeof privacy?.analyticsProcessingAllowed === 'function') return privacy.analyticsProcessingAllowed() === true;
      return typeof privacy?.currentVisitorConsent === 'function' && privacy.currentVisitorConsent()?.analytics === 'yes';
    } catch (_) { return false; }
  }

  function emitNavigation(root, link, event, environment = window) {
    if (root.dataset.entityAnalyticsEnabled !== 'true' || event.isTrusted !== true || event.button !== 0
        || !analyticsConsent(environment) || !Array.isArray(environment.dataLayer)) return false;
    const params = new URLSearchParams(environment.location.search);
    if (params.has('preview_theme_id') || params.has('_fd') || environment.Shopify?.designMode === true) return false;
    const type = link.dataset.tsHomeEntityType;
    const key = entityKey(link.dataset.tsHomeEntityKey, type);
    const source = link.dataset.tsHomeEntitySource;
    if (!key || !['home_search_sheet', 'home_entity_card'].includes(source)) return false;
    // Decorations may contain only the existing preview/locale allowlist; the
    // canonical destination was independently validated before decoration.
    let canonical;
    try {
      const target = new URL(link.getAttribute('href'), environment.location.origin);
      if (target.origin !== environment.location.origin || target.username || target.password || target.hash) return false;
      if ([...target.searchParams.keys()].some((name) => !['preview_theme_id', '_fd', 'pb', 'locale', 'currency'].includes(name))) return false;
      canonical = entityUrl(target.pathname, type, environment.location.origin);
    } catch (_) { return false; }
    if (!canonical || typeof environment.crypto?.randomUUID !== 'function') return false;
    const eventId = `ts_${environment.crypto.randomUUID().replace(/-/g, '')}`;
    if (!/^ts_[a-f0-9]{32}$/.test(eventId)) return false;
    environment.dataLayer.push({ event: 'entity_navigation_click', event_id: eventId,
      page_type: 'home', target_entity_key: key, source });
    return true;
  }

  function init(root, { buildEntityUrl }) {
    if (initialized.has(root)) return;
    initialized.add(root);
    root.addEventListener('click', (event) => {
      const link = event.target?.closest?.('[data-ts-home-entity-link]');
      if (link && root.contains(link)) emitNavigation(root, link, event);
    });
    const origin = window.location.origin;
    const url = cacheUrl(root.dataset.entityRecommendationsUrl, origin);
    if (!url) return;
    load(url, origin).then((cache) => {
      if (!cache || cache.expires <= Date.now()) return;
      const originals = [];
      root.querySelectorAll('[data-ts-home-recommendation-group]').forEach((group) => {
        const type = group.dataset.tsHomeRecommendationGroup;
        const container = group.querySelector('.ts-home-search-sheet__chips');
        const items = cache.items.filter((item) => item.type === type)
          .sort((a, b) => b.count - a.count || b.days - a.days || a.key.localeCompare(b.key, 'en'));
        if (!container || !items.length) return;
        originals.push({ container, nodes: Array.from(container.childNodes) });
        const fragment = document.createDocumentFragment();
        for (const item of items) {
          const href = buildEntityUrl(item.url, type);
          if (!href) continue;
          const link = document.createElement('a');
          link.className = 'ts-home-chip'; link.href = href; link.textContent = item.label;
          link.setAttribute('data-ts-home-entity-link', '');
          link.dataset.tsHomeEntityType = type; link.dataset.tsHomeEntityKey = item.key;
          link.dataset.tsHomeEntitySource = 'home_search_sheet';
          fragment.appendChild(link);
        }
        container.replaceChildren(fragment);
      });
      // An open tab cannot keep a stale ranking beyond its source lifetime.
      setTimeout(() => originals.forEach(({ container, nodes }) => container.replaceChildren(...nodes)),
        Math.max(0, Math.min(cache.expires - Date.now(), 2147483647)));
    });
  }

  window.TimeseekEntityRecommendations = { entityKey, entityUrl, cacheUrl, parseCache, emitNavigation, init };
})();
